import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { assertSportsTradingAllowed } from './sportsTradingGuard';
import type { ClassifiedTradeRequestConfig } from './tradeRequestGateway';

type InterceptedTradeRequestConfig = InternalAxiosRequestConfig &
    Partial<Pick<ClassifiedTradeRequestConfig, 'sportsTradingScope'>>;

const TRADE_ACTION_METHODS = new Set(['delete', 'patch', 'post', 'put']);
// TODO: Replace this path-regex backstop with explicit request classification so each
// trade endpoint opts in to, or out of, GeoComply enforcement at its call site.
const EVENT_ORDER_PATH = /^\/(?:v1\/)?users\/[^/]+\/orders(?:\/.*)?$/;
const PORTFOLIO_ORDER_PATH = /^\/(?:trade-api\/v2\/)?portfolio\/(?:events\/)?orders(?:\/.*)?$/;
const EVENT_ORDER_GROUP_PATH = /^\/(?:trade-api\/v2\/)?portfolio\/(?:events\/)?order_groups(?:\/.*)?$/;
const COMMUNICATIONS_TRADE_PATH = /^\/(?:v1\/)?communications\/(?:quotes|rfqs)(?:\/.*)?$/;
const LEGACY_RFQ_PATH = /^\/(?:v1\/)?rfqs(?:\/.*)?$/;
const BLOCK_TRADE_PATH = /^\/(?:v1\/)?communications\/block-trade-proposals(?:\/.*)?$/;

function getPathname(url: string): string {
    try {
        return new URL(url, 'https://trade-request.local').pathname;
    } catch {
        return url.split('?')[0] ?? '';
    }
}

export function isGeoComplyProtectedTradeRequest(method: string | undefined, url: string | undefined): boolean {
    if (!method || !url) return false;

    const normalizedMethod = method.toLowerCase();
    if (!TRADE_ACTION_METHODS.has(normalizedMethod)) return false;

    const pathname = getPathname(url);
    // Order cancellation reduces exposure and must remain available even
    // when the user cannot open or amend a sports position.
    if (
        normalizedMethod === 'delete' &&
        (EVENT_ORDER_PATH.test(pathname) ||
            PORTFOLIO_ORDER_PATH.test(pathname) ||
            EVENT_ORDER_GROUP_PATH.test(pathname))
    ) {
        return false;
    }

    return (
        EVENT_ORDER_PATH.test(pathname) ||
        PORTFOLIO_ORDER_PATH.test(pathname) ||
        EVENT_ORDER_GROUP_PATH.test(pathname) ||
        COMMUNICATIONS_TRADE_PATH.test(pathname) ||
        LEGACY_RFQ_PATH.test(pathname) ||
        BLOCK_TRADE_PATH.test(pathname)
    );
}

function enforceTradeGeoCompliance(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    if (!isGeoComplyProtectedTradeRequest(config.method, config.url)) return config;

    const { sportsTradingScope } = config as InterceptedTradeRequestConfig;
    assertSportsTradingAllowed(sportsTradingScope);
    return config;
}

export function attachTradeGeoComplianceInterceptor(client: AxiosInstance): void {
    client.interceptors.request.use(enforceTradeGeoCompliance, undefined, {
        runWhen: (config) => isGeoComplyProtectedTradeRequest(config.method, config.url),
    });
}
